<template>
    <v-dialog v-model="dialog" max-width="700px" persistent scrollable>
        <v-card>
            <v-card-title class="headline primary white--text">
                <v-icon left color="white">mdi-upload</v-icon>
                Upload Meeting Resource
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-form ref="uploadForm" v-model="formValid" lazy-validation>
                    <!-- Resource Type Mode Selection -->
                    <div class="mb-6">
                        <v-btn-toggle v-model="resourceMode" mandatory color="primary" class="mode-toggle mb-4">
                            <v-btn value="file" class="mode-btn">
                                <v-icon left>mdi-file-upload</v-icon>
                                File Upload
                            </v-btn>
                            <v-btn value="url" class="mode-btn">
                                <v-icon left>mdi-link</v-icon>
                                URL Resource
                            </v-btn>
                        </v-btn-toggle>
                    </div>

                    <!-- File Upload Section -->
                    <div v-if="resourceMode === 'file'" class="mb-6">
                        <v-file-input
                            v-model="formData.file"
                            :rules="fileRules"
                            label="Select file to upload *"
                            outlined
                            show-size
                            prepend-icon=""
                            prepend-inner-icon="mdi-paperclip"
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.png,.jpg,.jpeg"
                            :loading="uploading"
                            @change="onFileChange"
                        >
                            <template v-slot:selection="{ text }">
                                <v-chip color="primary" outlined small close @click:close="formData.file = null">
                                    <v-icon left small>{{ getFileIcon(formData.file) }}</v-icon>
                                    {{ text }}
                                </v-chip>
                            </template>
                        </v-file-input>

                        <!-- File Upload Progress -->
                        <v-progress-linear
                            v-if="uploadProgress > 0 && uploadProgress < 100"
                            :value="uploadProgress"
                            color="primary"
                            height="6"
                            rounded
                            class="mt-2"
                        ></v-progress-linear>
                    </div>

                    <!-- URL Input Section -->
                    <div v-if="resourceMode === 'url'" class="mb-6">
                        <v-text-field
                            v-model="formData.url"
                            :rules="urlRules"
                            label="Resource URL *"
                            outlined
                            dense
                            prepend-inner-icon="mdi-link"
                            placeholder="https://docs.google.com/..."
                            hint="Enter URL for external resources like Google Sheets, documents, etc."
                            persistent-hint
                            :disabled="uploading"
                        ></v-text-field>
                    </div>

                    <!-- Document Information -->
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="formData.name"
                                :rules="nameRules"
                                label="Resource Name *"
                                outlined
                                dense
                                prepend-inner-icon="mdi-file-document-outline"
                                maxlength="100"
                                counter
                                :disabled="uploading"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="12">
                            <v-select
                                v-model="formData.category"
                                :items="categoryOptions"
                                :rules="categoryRules"
                                label="Category *"
                                outlined
                                dense
                                prepend-inner-icon="mdi-tag"
                                :disabled="uploading"
                            ></v-select>
                        </v-col>

                        <v-col cols="12">
                            <v-textarea
                                v-model="formData.description"
                                label="Description"
                                outlined
                                rows="3"
                                prepend-inner-icon="mdi-text"
                                maxlength="500"
                                counter
                                hint="Brief description of the resource"
                                persistent-hint
                                :disabled="uploading"
                            ></v-textarea>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text @click="close" :disabled="uploading">
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    @click="uploadResource"
                    :loading="uploading"
                    :disabled="!formValid || uploading"
                >
                    <v-icon left>mdi-upload</v-icon>
                    Upload
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'UploadMeetingResourceDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        meetingId: {
            type: Number,
            required: true
        },
        meetingCompanyId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            formValid: false,
            uploading: false,
            uploadProgress: 0,
            resourceMode: 'file',
            formData: {
                file: null,
                url: '',
                name: '',
                description: '',
                category: 'Other',
                company_id: this.meetingCompanyId,
            },
            fileRules: [
                v => this.resourceMode !== 'file' || !!v || 'File is required',
                v => !v || v.size < 50000000 || 'File size should be less than 50 MB'
            ],
            urlRules: [
                v => this.resourceMode !== 'url' || !!v || 'URL is required',
                v => {
                    if (this.resourceMode !== 'url' || !v) return true;
                    try {
                        new URL(v);
                        return true;
                    } catch {
                        return 'Please enter a valid URL';
                    }
                }
            ],
            nameRules: [
                v => !!v || 'Resource name is required',
                v => (v && v.length >= 3) || 'Name must be at least 3 characters',
                v => (v && v.length <= 100) || 'Name must be less than 100 characters'
            ],
            categoryRules: [
                v => !!v || 'Category is required'
            ],
            categoryOptions: ['SOP', 'Template', 'EOD', 'SOD', 'Guide', 'Other']
        };
    },
    computed: {
        dialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        }
    },
    watch: {
        dialog(val) {
            if (!val) {
                this.resetForm();
            } else {
                // Set company_id when dialog opens
                this.formData.company_id = this.meetingCompanyId;
            }
        },
        resourceMode() {
            // Reset validation when switching modes
            if (this.$refs.uploadForm) {
                this.$refs.uploadForm.resetValidation();
            }
        }
    },
    methods: {
        onFileChange(file) {
            if (file) {
                // Auto-fill name if empty
                if (!this.formData.name) {
                    this.formData.name = file.name.replace(/\.[^/.]+$/, '');
                }
            }
        },

        getFileIcon(file) {
            if (!file) return 'mdi-file-document-outline';

            const ext = file.name.split('.').pop().toLowerCase();
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                png: 'mdi-file-image',
                jpg: 'mdi-file-image',
                jpeg: 'mdi-file-image'
            };

            return iconMap[ext] || 'mdi-file-document-outline';
        },

        async uploadResource() {
            if (!this.$refs.uploadForm.validate()) {
                return;
            }

            this.uploading = true;
            this.uploadProgress = 0;

            try {
                let response;

                if (this.resourceMode === 'file' && this.formData.file) {
                    // Handle file upload
                    const formData = new FormData();
                    formData.append('file', this.formData.file);
                    formData.append('name', this.formData.name || this.formData.file.name);
                    formData.append('description', this.formData.description || '');
                    formData.append('category', this.formData.category || 'Other');
                    formData.append('company_id', this.formData.company_id || '');
                    formData.append('type', 'file');
                    formData.append('source', 'meeting');
                    formData.append('meeting_id', this.meetingId);

                    response = await this.$axios.post(`/api/meetings/${this.meetingId}/upload-resource`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: (progressEvent) => {
                            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        }
                    });
                } else if (this.resourceMode === 'url' && this.formData.url) {
                    // Handle URL resource
                    const payload = {
                        name: this.formData.name,
                        url: this.formData.url,
                        description: this.formData.description || '',
                        category: this.formData.category || 'Other',
                        company_id: this.formData.company_id || null,
                        type: 'url',
                        file_type: 'url',
                        source: 'meeting',
                        meeting_id: this.meetingId
                    };

                    response = await this.$axios.post(`/api/meetings/${this.meetingId}/upload-resource`, payload);
                    this.uploadProgress = 100;
                } else {
                    throw new Error('Invalid resource data');
                }

                if (response.data.response || response.data.success) {
                    this.$emit('resource-uploaded', response.data.data);
                    this.$toast.success(`Resource ${this.resourceMode === 'file' ? 'uploaded' : 'added'} successfully`);
                    this.close();
                } else {
                    throw new Error(response.data.message || 'Upload failed');
                }
            } catch (error) {
                console.error('Upload error:', error);
                this.$toast.error(error.message || 'Failed to upload resource');
            } finally {
                this.uploading = false;
                this.uploadProgress = 0;
            }
        },

        resetForm() {
            this.formData = {
                file: null,
                url: '',
                name: '',
                description: '',
                category: 'Other',
                company_id: this.meetingCompanyId,
            };
            this.resourceMode = 'file';
            this.uploadProgress = 0;

            if (this.$refs.uploadForm) {
                this.$refs.uploadForm.resetValidation();
            }
        },

        close() {
            this.dialog = false;
        }
    }
};
</script>

<style scoped>
.mode-toggle {
    width: 100%;
}

.mode-btn {
    flex: 1;
    text-transform: none;
    font-weight: 500;
}
</style>
